import { Chip } from '../ui/primitives';
import { PeriodChips } from '../period/PeriodPicker';
import { MAX_REPORT_DAYS, storeToday, type AppliedPeriod, type PeriodChoice } from '../../utils/reportsView';
import { matchPeriod, type DateRange } from '../../utils/periods';

/**
 * Rediseño · Bloque F — el periodo de un reporte. Es el ÚNICO filtro real que tiene `owner-overview`: la
 * tienda no se elige (N5; el alcance lo decide el servidor). En R1 va además «Comparar con el periodo
 * anterior» (P-030), que no es un filtro del servidor (P-032, I9): apagado solo deja de dibujar las flechas
 * y la comparación; no pide nada.
 *
 * P-075 · El selector global de periodos (FI-90): los atajos aprobados a la vista —Hoy · Ayer · Antier ·
 * Últimos 7 días · Este mes— y «Otro periodo», que abre UN selector (Día | Semana | Mes | Personalizado) con
 * un solo calendario de rango. El tope sigue siendo de 93 días (los «3 meses completos» caben siempre) y lo que
 * se pide al servidor no cambia: un rango explícito. Elegido un periodo, se pide enseguida.
 *
 * P-084 (K8) · «Hoy» es el de la tienda de referencia del perfil (`today`, que resuelve la página con la zona de la
 * primera tienda financiera), no el de Ciudad de México fijo ni el del equipo.
 */
export function ReportsPeriod({ applied, onApply, compare, today = storeToday() }: {
  applied: AppliedPeriod;
  onApply: (period: AppliedPeriod) => void;
  /** «Comparar con el periodo anterior» (P-030, solo en R1): no es un filtro del servidor, solo de pantalla. */
  compare?: { on: boolean; onToggle: () => void };
  /** HOY en la zona de referencia de Reportes ('YYYY-MM-DD'). */
  today?: string;
}) {
  const apply = (range: DateRange) => onApply({ choice: choiceOf(range, today), from: range.from, to: range.to });
  return (
    <div className="n3-reports__period">
      <PeriodChips maxDays={MAX_REPORT_DAYS} onChange={apply} today={today} value={{ from: applied.from, to: applied.to }} />
      {compare && (
        <div className="n3-reports__chips">
          <Chip onClick={compare.onToggle} selected={compare.on}>Comparar con el periodo anterior</Chip>
        </div>
      )}
    </div>
  );
}

/** El nombre corto con el que Reportes recuerda un periodo (los de P-030 tienen el suyo; el resto es «otro»). */
function choiceOf(range: DateRange, today: string): PeriodChoice {
  const named = matchPeriod(range, today, ['hoy', 'ultimos-7', 'este-mes']);
  return named === 'hoy' ? 'hoy' : named === 'ultimos-7' ? 'semana' : named === 'este-mes' ? 'mes' : 'otro';
}
